/**
 * Precision zoom: Power BI-style range bars along the X and Y axes.
 *
 * A zoom, not a filter: it only moves the visible window, like drag-zoom with
 * exact handles and a Y axis. Filtering is what slicers are for
 * (`../slicers.ts`). Style `precision_zoom`, link `pz=1`; links from when it
 * was called "axis sliders" carry `asl=1`, which still turns it on.
 *
 * Drag a handle to narrow what is in view, drag the band between them to pan,
 * double-click a bar to put that axis back. Every move is a scale change on the
 * uPlot instance already on screen - **nothing is fetched**, the same as a
 * drag-zoom (`zoom.ts`).
 *
 * A uPlot plugin rather than code in each renderer, so any Line or Scatter
 * instance can take it. It reserves its own room - extra padding under the X
 * axis and right of the plot - and draws in it, so it never sits on top of axis
 * labels. The track behind each bar is a faint histogram of the data on that
 * axis, so you can see where the points are before you slice.
 *
 * The Y window survives uPlot's autoscaling (which re-fits Y whenever X moves)
 * by wrapping the Y scale's range function: while a Y window is set, it wins.
 */

import type uPlot from 'uplot';

type Axis = 'x' | 'y';

/** uPlot's resolved scale-range function. */
type RangeFn = (self: uPlot, min: number, max: number, key: string) => [number | null, number | null];

interface Span {
  min: number;
  max: number;
}

/** Room reserved for the bars, in CSS px. */
const BAR_ROOM = 22;
/** Bar thickness, in CSS px. */
const BAR = 12;
/** Histogram buckets drawn behind each bar. */
const BUCKETS = 40;
/** The narrowest window a bar allows, as a share of its axis's extent. */
const MIN_SHARE = 0.005;

/** Finite min/max of the values, or null when there are none. */
function extentOf(arrays: ArrayLike<number | null | undefined>[]): Span | null {
  let min = Infinity;
  let max = -Infinity;
  for (const arr of arrays) {
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  return min < max ? { min, max } : null;
}

/** Share of values per bucket across the extent, scaled 0-1 to the fullest bucket. */
function histogram(arrays: ArrayLike<number | null | undefined>[], span: Span): number[] {
  const counts = new Array<number>(BUCKETS).fill(0);
  const width = span.max - span.min;
  for (const arr of arrays) {
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      const b = Math.min(BUCKETS - 1, Math.max(0, Math.floor(((v - span.min) / width) * BUCKETS)));
      counts[b]!++;
    }
  }
  const top = Math.max(1, ...counts);
  return counts.map((c) => c / top);
}

/** The arrays each axis is sliced over: X values, and every left-axis series. */
function axisData(u: uPlot, axis: Axis): ArrayLike<number | null>[] {
  if (axis === 'x') return [u.data[0] as ArrayLike<number | null>];
  const out: ArrayLike<number | null>[] = [];
  u.series.forEach((s, i) => {
    if (i > 0 && s.scale === 'y' && s.show !== false) out.push(u.data[i] as ArrayLike<number | null>);
  });
  return out;
}

interface Bar {
  el: HTMLElement;
  band: HTMLElement;
  hist: HTMLElement;
}

export function precisionZoomPlugin(): uPlot.Plugin {
  const extent: Record<Axis, Span | null> = { x: null, y: null };
  /** The Y window the user set, or null for "whatever uPlot fits". */
  let yWindow: Span | null = null;
  const bars: Partial<Record<Axis, Bar>> = {};
  let cleanup: Array<() => void> = [];

  /** Where the axis's current view sits within its extent, as 0-1 shares. */
  const shares = (u: uPlot, axis: Axis): [number, number] => {
    const ext = extent[axis];
    if (!ext) return [0, 1];
    const view = axis === 'y' ? yWindow : { min: u.scales.x!.min!, max: u.scales.x!.max! };
    if (!view || view.min == null || view.max == null) return [0, 1];
    const w = ext.max - ext.min;
    const a = Math.min(1, Math.max(0, (view.min - ext.min) / w));
    const b = Math.min(1, Math.max(0, (view.max - ext.min) / w));
    return b - a < MIN_SHARE ? [a, Math.min(1, a + MIN_SHARE)] : [a, b];
  };

  const paint = (u: uPlot): void => {
    for (const axis of ['x', 'y'] as Axis[]) {
      const bar = bars[axis];
      if (!bar) continue;
      const [a, b] = shares(u, axis);
      const full = a <= 0.0005 && b >= 0.9995;
      bar.el.classList.toggle('is-narrowed', !full);
      if (axis === 'x') {
        bar.band.style.left = `${a * 100}%`;
        bar.band.style.width = `${(b - a) * 100}%`;
      } else {
        // Y runs bottom-up, like the axis beside it.
        bar.band.style.bottom = `${a * 100}%`;
        bar.band.style.height = `${(b - a) * 100}%`;
      }
    }
  };

  /** Place the bars in the room the plugin reserved, beside the plot area. */
  const layout = (u: uPlot): void => {
    const left = u.over.offsetLeft;
    const top = u.over.offsetTop;
    const w = u.over.offsetWidth;
    const h = u.over.offsetHeight;
    const wrap = u.over.parentElement!;
    const x = bars.x;
    if (x) {
      Object.assign(x.el.style, {
        left: `${left}px`,
        width: `${w}px`,
        top: `${wrap.offsetHeight - BAR - 4}px`,
        height: `${BAR}px`,
      });
    }
    const y = bars.y;
    if (y) {
      Object.assign(y.el.style, {
        left: `${wrap.offsetWidth - BAR - 4}px`,
        width: `${BAR}px`,
        top: `${top}px`,
        height: `${h}px`,
      });
    }
  };

  const drawHistograms = (u: uPlot): void => {
    for (const axis of ['x', 'y'] as Axis[]) {
      const bar = bars[axis];
      const ext = extent[axis];
      if (!bar) continue;
      bar.hist.innerHTML = '';
      if (!ext) continue;
      const shares = histogram(axisData(u, axis), ext);
      for (const s of shares) {
        const cell = document.createElement('span');
        cell.style.setProperty('--fill', String(Math.max(0.04, s)));
        bar.hist.appendChild(cell);
      }
    }
  };

  const measure = (u: uPlot): void => {
    extent.x = extentOf(axisData(u, 'x'));
    extent.y = extentOf(axisData(u, 'y'));
  };

  /** Apply a new window for one axis - the only way the bars change the chart. */
  const apply = (u: uPlot, axis: Axis, span: Span): void => {
    if (axis === 'x') {
      u.setScale('x', span);
    } else {
      yWindow = span;
      u.setScale('y', span);
    }
  };

  /** Back to the whole extent on one axis. */
  const reset = (u: uPlot, axis: Axis): void => {
    const ext = extent[axis];
    if (!ext) return;
    if (axis === 'y') {
      yWindow = null;
      // Re-fit through the scale's own range, as a fresh chart would.
      const range = u.scales.y!.range as RangeFn | undefined;
      const [min, max] = range ? range(u, ext.min, ext.max, 'y') : [ext.min, ext.max];
      u.setScale('y', { min: min ?? ext.min, max: max ?? ext.max });
      return;
    }
    apply(u, 'x', ext);
  };

  const buildBar = (u: uPlot, axis: Axis): Bar => {
    const el = document.createElement('div');
    el.className = `precision-zoom precision-zoom-${axis}`;
    el.title = `Drag the ends to narrow ${axis.toUpperCase()}, the middle to move it - double-click to reset`;
    const hist = document.createElement('div');
    hist.className = 'precision-zoom-hist';
    const band = document.createElement('div');
    band.className = 'precision-zoom-band';
    const lo = document.createElement('span');
    lo.className = 'precision-zoom-handle is-lo';
    const hi = document.createElement('span');
    hi.className = 'precision-zoom-handle is-hi';
    band.append(lo, hi);
    el.append(hist, band);

    const onDown = (e: PointerEvent): void => {
      const ext = extent[axis];
      if (!ext || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const mode = target === lo ? 'lo' : target === hi ? 'hi' : target === band ? 'pan' : 'jump';
      const rect = el.getBoundingClientRect();
      const along = (ev: PointerEvent): number =>
        axis === 'x'
          ? (ev.clientX - rect.left) / rect.width
          : (rect.bottom - ev.clientY) / rect.height;
      const [a0, b0] = shares(u, axis);
      const start = along(e);
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // Not an active pointer (a synthetic or already-ended one) - the drag
        // still works through the listeners below, just without capture.
      }
      el.classList.add('is-dragging');

      let frame = 0;
      const move = (ev: PointerEvent): void => {
        let a = a0;
        let b = b0;
        const p = Math.min(1, Math.max(0, along(ev)));
        if (mode === 'lo') a = Math.min(p, b0 - MIN_SHARE);
        else if (mode === 'hi') b = Math.max(p, a0 + MIN_SHARE);
        else if (mode === 'pan') {
          const d = Math.min(1 - b0, Math.max(-a0, p - start));
          a = a0 + d;
          b = b0 + d;
        } else {
          // Pressed on the bare track: centre the current window there.
          const half = (b0 - a0) / 2;
          const c = Math.min(1 - half, Math.max(half, p));
          a = c - half;
          b = c + half;
        }
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const w = ext.max - ext.min;
          apply(u, axis, { min: ext.min + a * w, max: ext.min + b * w });
        });
      };
      const up = (ev: PointerEvent): void => {
        if (el.hasPointerCapture(ev.pointerId)) el.releasePointerCapture(ev.pointerId);
        el.classList.remove('is-dragging');
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
      };
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      if (mode === 'jump') move(e);
    };
    const onDbl = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      reset(u, axis);
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('dblclick', onDbl);
    cleanup.push(() => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('dblclick', onDbl);
      el.remove();
    });
    u.over.parentElement!.appendChild(el);
    return { el, band, hist };
  };

  return {
    opts: (_u, opts) => {
      // Reserve the bars' room: below the X axis and right of the plot.
      const pad = (opts.padding ?? [null, null, null, null]) as Array<uPlot.Padding[number]>;
      const plus = (side: uPlot.Padding[number], extra: number): uPlot.Padding[number] =>
        typeof side === 'number' ? side + extra : extra + 8;
      opts.padding = [pad[0] ?? null, plus(pad[1] ?? null, BAR_ROOM), plus(pad[2] ?? null, BAR_ROOM), pad[3] ?? null];
    },
    hooks: {
      init: [
        (u) => {
          // While a Y window is set, it is the Y range - uPlot re-fits Y every
          // time X moves, and would otherwise undo the slider on each pan.
          const y = u.scales.y;
          if (!y) return;
          const base = y.range as RangeFn | undefined;
          y.range = ((self: uPlot, min: number, max: number, key: string) =>
            yWindow
              ? [yWindow.min, yWindow.max]
              : base
                ? base(self, min, max, key)
                : [min, max]) as RangeFn;
        },
      ],
      ready: [
        (u) => {
          measure(u);
          bars.x = buildBar(u, 'x');
          if (u.scales.y) bars.y = buildBar(u, 'y');
          drawHistograms(u);
          layout(u);
          paint(u);
        },
      ],
      setData: [
        (u) => {
          measure(u);
          drawHistograms(u);
          paint(u);
        },
      ],
      setSize: [(u) => layout(u)],
      setScale: [
        (u, key) => {
          if (key === 'x' || key === 'y') paint(u);
        },
      ],
      destroy: [
        () => {
          for (const fn of cleanup) fn();
          cleanup = [];
        },
      ],
    },
  };
}
